import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Container,
  Typography,
  IconButton,
  CardHeader,
  CardContent,
  Box,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid"; 
import DownloadIcon from "@mui/icons-material/GetApp";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { fDateTime } from "../utils/formatTime";

import {
  getAccessToken,
  RefreshToken,
  handleAuthenticationFailure,
} from "../AuthenticationData.ts";

const TOAST_OPTS = { position: "top-center", autoClose: 1000 };

// --- vCard Helpers matched to Controller Keys ---
const escapeVCardText = (v) => v ? String(v).replace(/\\/g, "\\\\").replace(/\r\n|\r|\n/g, "\\n").replace(/;/g, "\\;").replace(/,/g, "\\,") : "";
const safeFileName = (n) => {
  const c = String(n || "contact").trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, "_").replace(/\s+/g, " ").slice(0, 60);
  return c.length ? c : "contact";
};

const buildVCard = ({ name, phone, event }) => {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `FN:${escapeVCardText(name || "Unknown")}`, 
    phone ? `TEL;TYPE=CELL:${escapeVCardText(phone)}` : null,
    event ? `NOTE:${escapeVCardText(event)}` : null,
    "END:VCARD"
  ].filter(Boolean);
  return lines.join("\r\n");
};

const LeadsPage = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  useEffect(() => {
    let alive = true;
    const fetchLeads = async () => {
      setLoading(true);
      const token = getAccessToken();
      if (!token) { handleAuthenticationFailure(navigate, toast); return; }
      try {
        const res = await axios.get("/api/VisitorContact/GetMyLeads", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setLeads(res?.data?.items || []);
      } catch (error) {
        if (error?.response?.status === 401) {
          const refreshed = await RefreshToken();
          if (refreshed) {
            const res2 = await axios.get("/api/VisitorContact/GetMyLeads", {
              headers: { Authorization: `Bearer ${getAccessToken()}` },
            });
            if (alive) setLeads(res2?.data?.items || []);
          }
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchLeads();
    return () => { alive = false; };
  }, [navigate]);

  // --- Mark as Read + Download logic ---
  const handleLeadAction = async (lead) => {
    // 1. Trigger Download
    downloadVCard(lead);

    // 2. If already read, skip API call
    if (lead.status === "Read") return;

    // 3. Mark as read on backend and update local UI
    const token = getAccessToken();
    try {
      await axios.post(`/api/VisitorContact/MarkAsRead/${lead.id}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Update local state instantly
      setLeads((prev) => 
        prev.map((item) => item.id === lead.id ? { ...item, status: "Read" } : item)
      );
      window.dispatchEvent(new Event("refreshLeadsCount")); 
    } catch (e) {
      console.error("Failed to mark as read", e);
    }
  };

  const downloadVCard = (lead) => {
    if (!lead?.phone) {
      toast.error("No phone number", TOAST_OPTS);
      return;
    }
    const vCard = buildVCard({ 
        name: lead.name, 
        phone: lead.phone, 
        event: lead.event 
    });
    const blob = new Blob([vCard], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFileName(lead.name)}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded.", TOAST_OPTS);
  };

  const columns = [
    {
      field: "name",
      headerName: "Full Name",
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => {
        const isNew = params.row?.status === "New";
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {/* Visual Indicator: Blue Dot */}
            {isNew && (
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: 'primary.main',
                  boxShadow: (theme) => `0 0 0 2px ${theme.palette.background.paper}, 0 0 8px ${theme.palette.primary.main}`
                }} 
              />
            )}
            <Box sx={{ ml: isNew ? 0 : 2.5 /* Keep alignment if no dot */ }}>
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  color: isNew ? 'text.primary' : 'text.secondary',
                  fontWeight: isNew ? 'bold' : 'medium'
                }}
              >
                {params.row?.name || "Unknown"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "event",
      headerName: "Event Source",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {params.row?.event || "Direct Contact"}
        </Typography>
      ),
    },
    {
      field: "dateSharedUtc",
      headerName: "Date",
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row?.dateSharedUtc ? fDateTime(params.row.dateSharedUtc) : "—"}
        </Typography>
      )
    },
    {
      field: "action",
      headerName: "Action",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: () => (
        <IconButton color="primary" size="small">
          <DownloadIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  return (
    <Container sx={{ mt: 3 }}>
      <Typography variant="h5" sx={{ mb: 4 }}>
        Leads
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader
              title="Leads Directory"
              subheader="New leads are marked with an orange dot. Click a row to download and mark as read."
            />
            
            <CardContent>
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={leads}
                  columns={columns}
                  loading={loading}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5]}
                  disableRowSelectionOnClick
                  onRowClick={(params) => handleLeadAction(params.row)}
                  getRowId={(row) => row.id}
                  sx={{
                    border: "none",
                    "& .MuiDataGrid-columnHeaders": {
                      backgroundColor: "rgba(145, 158, 171, 0.12)",
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px dashed rgba(145, 158, 171, 0.2)",
                      display: "flex",
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-row": {
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(145, 158, 171, 0.08)",
                      },
                    },
                    "& .MuiDataGrid-footerContainer": {
                      borderTop: "none",
                    },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4} />
      </Grid>
    </Container>
  );
};

export default LeadsPage;
